"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useRouter } from "next/navigation";
import type { User } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import {
  fetchActionPermissions,
  DENIED_ACTIONS,
  ACTION_PERMISSION_MAP,
  type ActionPermissions,
  type PermissionScope,
} from "@/lib/supabase/permissionQueries";

export type UserRole = 'admin' | 'manager' | 'member' | 'viewer' | 'external';

export interface UserPermissions {
  role: UserRole;
  visiblePages: string[] | null; // null = all pages visible
  canEdit: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
}

const DEFAULT_PERMISSIONS: UserPermissions = {
  role: 'member',
  visiblePages: null,
  canEdit: true,
  canDelete: false,
  canManageUsers: false,
};

function resolvePermissions(user: User | null): UserPermissions {
  if (!user) return { ...DEFAULT_PERMISSIONS, role: 'viewer', canEdit: false };

  const role = (user.app_metadata?.role as UserRole) ?? 'member';

  switch (role) {
    case 'admin':
      return { role, visiblePages: null, canEdit: true, canDelete: true, canManageUsers: true };
    case 'manager':
      return { role, visiblePages: null, canEdit: true, canDelete: true, canManageUsers: false };
    case 'member':
      return { role, visiblePages: null, canEdit: true, canDelete: false, canManageUsers: false };
    case 'viewer':
      return { role, visiblePages: null, canEdit: false, canDelete: false, canManageUsers: false };
    case 'external': {
      const pages = (user.app_metadata?.visible_pages as string[]) ?? null;
      return { role, visiblePages: pages, canEdit: false, canDelete: false, canManageUsers: false };
    }
    default:
      return DEFAULT_PERMISSIONS;
  }
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  permissions: UserPermissions;
  actionPermissions: ActionPermissions;
  canPerformAction: (actionId: string) => boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

// Fail closed while permissions are still loading, and for a signed-out user.
// This used to be roleDefaults('member') — editor with CSV export — which meant
// every screen rendered its edit and export controls before anyone had checked.
const DEFAULT_ACTION_PERMISSIONS = DENIED_ACTIONS;

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  permissions: DEFAULT_PERMISSIONS,
  actionPermissions: DEFAULT_ACTION_PERMISSIONS,
  canPerformAction: () => true,
  isAdmin: false,
  signOut: async () => { /* no-op */ },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionPerms, setActionPerms] = useState<ActionPermissions>(DEFAULT_ACTION_PERMISSIONS);
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    // Initial user load
    supabase.auth.getUser().then(({ data: { user: u } }) => {
      setUser(u);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  const permissions = useMemo(() => resolvePermissions(user), [user]);
  const isAdmin = permissions.role === 'admin';

  // Fetch action permissions when user loads
  useEffect(() => {
    if (!user) {
      setActionPerms(DENIED_ACTIONS);
      return;
    }
    fetchActionPermissions(user.id, permissions.role).then(setActionPerms);
  }, [user, permissions.role]);

  const canPerformAction = useCallback((actionId: string): boolean => {
    if (isAdmin) return true;
    const permKey = ACTION_PERMISSION_MAP[actionId];
    // An action id that carries no permission is not a permission-bearing
    // action (open a row, refresh a list). It is NOT the same thing as a
    // missing permission row, which now denies — see fetchActionPermissions.
    if (!permKey) return true;
    return actionPerms[permKey];
  }, [isAdmin, actionPerms]);

  const signOut = useCallback(async () => {
    try {
      // Use our API route for logout to properly handle cookies
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout API error:', error);
      // Fallback to direct Supabase logout
      await supabase.auth.signOut();
    }

    // Redirect to login page
    router.push("/auth/login");
    router.refresh();
  }, [supabase, router]);

  const value = useMemo(
    () => ({ user, loading, permissions, actionPermissions: actionPerms, canPerformAction, isAdmin, signOut }),
    [user, loading, permissions, actionPerms, canPerformAction, isAdmin, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Permissions for ONE app or entity type, resolved server-side.
 *
 * `useAuth().canPerformAction` answers at the global layer. A screen that
 * belongs to a specific app should ask with its own scope, because Gal can
 * grant editing in one app and withhold it in another — the whole point of
 * the layer. entity_type beats app beats global.
 *
 * Starts denied and stays denied until the resolver answers.
 */
export function useScopedActionPermissions(scope: PermissionScope) {
  const { user, permissions } = useAuth();
  const [perms, setPerms] = useState<ActionPermissions>(DENIED_ACTIONS);
  const [loading, setLoading] = useState(true);
  const { appId, entityType } = scope;

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setPerms(DENIED_ACTIONS);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchActionPermissions(user.id, permissions.role, { appId, entityType })
      .then((result) => { if (!cancelled) setPerms(result); })
      .catch(() => { if (!cancelled) setPerms(DENIED_ACTIONS); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, permissions.role, appId, entityType]);

  const can = useCallback((actionId: string): boolean => {
    if (permissions.role === 'admin') return true;
    const permKey = ACTION_PERMISSION_MAP[actionId];
    if (!permKey) return true;
    return perms[permKey];
  }, [permissions.role, perms]);

  return { permissions: perms, can, loading };
}
